import React from 'react';
import "./index.scss";

const CircularProgress = ({ progress, size = 150 }) => {
  // Ensure progress is between 0 and 100
  const normalizedProgress = Math.min(Math.max(progress || 0, 0), 100);

  // Calculate hexagon path points with flat top/bottom
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size / 2.2; // Larger radius to overlap and hide white spots
  const cornerRadius = 8; // Radius for rounded corners

  // Create hexagon points with flat top
  const hexagonPoints = [];
  for (let i = 0; i < 6; i++) {
    // Create points starting from right side (0°) going counter-clockwise
    const angle = (Math.PI / 3) * i;
    const x = centerX + radius * Math.cos(angle);
    const y = centerY + radius * Math.sin(angle);
    hexagonPoints.push({ x, y });
  }

  // Order points to go clockwise starting from top-right
  const reorderedPoints = [
    hexagonPoints[0], // top-right
    hexagonPoints[1], // right
    hexagonPoints[2], // bottom-right
    hexagonPoints[3], // bottom-left
    hexagonPoints[4], // left
    hexagonPoints[5], // top-left
  ];

  // Create path string for hexagon with rounded corners
  // Start at top-center (midpoint between top-left and top-right)
  const topLeftPoint = hexagonPoints[5];
  const topRightPoint = hexagonPoints[0];
  const topCenterX = (topLeftPoint.x + topRightPoint.x) / 2;
  const topCenterY = (topLeftPoint.y + topRightPoint.y) / 2;
  let hexagonPath = `M ${topCenterX} ${topCenterY}`;

  // Draw all 6 corners with rounded edges
  for (let i = 0; i < 6; i++) {
    const curr = reorderedPoints[i];
    const next = reorderedPoints[(i + 1) % 6];
    const prev = reorderedPoints[(i + 5) % 6];

    // Calculate direction vectors
    const dx1 = curr.x - prev.x;
    const dy1 = curr.y - prev.y;
    const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);

    const dx2 = next.x - curr.x;
    const dy2 = next.y - curr.y;
    const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);

    // Calculate rounded corner offset
    const offset = Math.min(cornerRadius, len1 / 3, len2 / 3);

    // Point before corner
    const beforeX = curr.x - (dx1 / len1) * offset;
    const beforeY = curr.y - (dy1 / len1) * offset;

    // Point after corner
    const afterX = curr.x + (dx2 / len2) * offset;
    const afterY = curr.y + (dy2 / len2) * offset;

    // Draw line to point before corner, then curve around corner
    hexagonPath += ` L ${beforeX} ${beforeY}`;
    hexagonPath += ` Q ${curr.x} ${curr.y} ${afterX} ${afterY}`;
  }

  // Close the path
  hexagonPath += ` Z`;

  // Calculate the actual path perimeter
  // The path starts from top-center, not a corner, so we need to account for that
  // Distance from top-center to first corner (top-right)
  const startToFirstCorner = Math.sqrt(
    Math.pow(reorderedPoints[0].x - topCenterX, 2) +
    Math.pow(reorderedPoints[0].y - topCenterY, 2)
  );

  // Calculate all side lengths
  let actualPerimeter = startToFirstCorner; // From start to first corner
  for (let i = 0; i < 5; i++) {
    const dist = Math.sqrt(
      Math.pow(reorderedPoints[i + 1].x - reorderedPoints[i].x, 2) +
      Math.pow(reorderedPoints[i + 1].y - reorderedPoints[i].y, 2)
    );
    actualPerimeter += dist;
  }
  // Last corner back to start (top-left to top-center)
  actualPerimeter += Math.sqrt(
    Math.pow(topCenterX - reorderedPoints[5].x, 2) +
    Math.pow(topCenterY - reorderedPoints[5].y, 2)
  );

  // Calculate stroke dash offset based on progress
  // Account for rounded corners making the path longer - multiply by 1.3
  const adjustedPerimeter = actualPerimeter * 1.3;
  const progressLength = (normalizedProgress / 100) * adjustedPerimeter;
  const strokeOffset = adjustedPerimeter - progressLength;

  console.log('Progress value:', normalizedProgress, 'Actual perimeter:', actualPerimeter, 'Adjusted:', adjustedPerimeter, 'Progress length:', progressLength);

  return (
    <div className="circular-progress-container" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="circular-progress-svg">
        {/* Background hexagon (subtle yellow) */}
        <path
          className="progress-background"
          d={hexagonPath}
          strokeWidth={3.5}
        />
        {/* Progress hexagon (bright yellow) */}
        <path
          className="progress-foreground"
          d={hexagonPath}
          strokeWidth={3.5}
          strokeDasharray={`${progressLength} ${adjustedPerimeter}`}
          strokeDashoffset={strokeOffset}
          style={{
            transition: 'stroke-dashoffset 0.5s ease'
          }}
        />
      </svg>
    </div>
  );
};

export default CircularProgress;
